import type { ActiveWorkspace } from '@/types/auth.types';

/**
 * arithmetic over an `ActiveWorkspace` and nothing that needs React — keep it that way, the tool
 * pages under `apps/` import from here to show the same numbers the app shows.
 */

/** Everything the workspace has, whatever the kind: monthly, extra and rolled over together. */
export const workspaceCreditsTotal = (workspace: ActiveWorkspace): number =>
  workspace.subscription?.creditsList?.reduce((acc, credit) => acc + credit.available, 0) || 0;

/**
 * What this member may still spend. An admin can cap a member per month, and the cap is what is left
 * of it, never more than the workspace itself holds. Without a cap the member may spend it all.
 */
export const availableCreditsForUser = (workspace: ActiveWorkspace): number => {
  const hasCreditsLimit = workspace.hasCustomCreditsLimit;
  const monthlyCreditsLimit = workspace.monthlyCreditsLimit;
  const defaultMonthlyAllocatedCredits = workspace.preferences?.defaultMonthlyAllocatedCredits;

  const creditsLimit = hasCreditsLimit
    ? monthlyCreditsLimit // null means unlimited, value means limited
    : (monthlyCreditsLimit ?? defaultMonthlyAllocatedCredits);
  const usedCredits = workspace.userWorkspaceCreditsUsage;
  const totalWorkspaceCredits = workspaceCreditsTotal(workspace);

  const availableCredits =
    creditsLimit !== null && creditsLimit !== undefined
      ? Math.min(Math.max(0, creditsLimit - usedCredits), totalWorkspaceCredits)
      : totalWorkspaceCredits;

  return availableCredits || 0;
};

/** An admin can hide the balance from members. Absent means shown. */
export const showsCreditsToMembers = (workspace: ActiveWorkspace): boolean =>
  workspace.preferences?.showCreditsToMembers ?? true;
